import { Building2 } from "lucide-react";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";

type Props = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  systemCodes: string[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
};

export function ExternalCompanyPicker({
  id,
  value,
  onChange,
  systemCodes,
  placeholder = "No external company",
  disabled,
  required,
}: Props) {
  const { data: companyTypes = [] } = useCategoryItems("company_type");
  const { data: allCompaniesResult } = useCompanyList({ page: 1, limit: 200 });

  const matchingTypeValues = new Set(
    companyTypes
      .filter((t) => {
        const sc =
          t.metadata && typeof t.metadata === "object"
            ? (t.metadata as Record<string, unknown>).systemCode
            : undefined;
        return typeof sc === "string" && systemCodes.includes(sc);
      })
      .map((t) => t.value),
  );

  const companies = (allCompaniesResult?.data ?? []).filter(
    (c) =>
      c.id === value ||
      (c.category === "EXTERNAL" && c.types.some((t) => matchingTypeValues.has(t))),
  );

  return (
    <div className="relative">
      <Building2
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        strokeWidth={1.5}
      />
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">{placeholder}</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.code})
          </option>
        ))}
      </select>
    </div>
  );
}
